import { useCallback, useEffect, useRef, useState } from "react";
import type { RazmerRow } from "../components/OSRazmerTable";
import { parseDecimalInput } from "../utils/decimalInput";

/**
 * Autosave for the three operator-typed Размерување cells on an RN (Кол. раз. / Отпад /
 * Растур). Leaving one of them with a changed value persists the whole RN through the
 * screen's ONE save path (`handleSaveIngredient`, quiet mode) and shows a spinner → ✓ on
 * that cell. Owner decision 2026-10-09.
 *
 * - "Changed" = differs from the last saved picture (`baselineRef`), compared as numbers so
 *   "12.5" typed and "12,5" echoed back by the server are the same value.
 * - `canSave` is checked right before each PUT — the "Штиклирај ✓" rule and the other
 *   save guards stay in force; a blocked cell just stays dirty for the manual Save.
 * - Never two PUTs at once: a blur during a running save is queued and ONE follow-up save
 *   runs afterwards with the latest rows, so no edit is lost and nothing runs in parallel.
 */
export type RazmerAutosaveField = "measuredQty" | "waste" | "rastur";
export type RazmerCellSaveStatus = "saving" | "saved";
export type RazmerCellSaveStatusMap = Record<string, RazmerCellSaveStatus>;

export const RAZMER_AUTOSAVE_FIELDS: RazmerAutosaveField[] = ["measuredQty", "waste", "rastur"];

/** Stable per-cell key (osRowId is the saved rowKey; rows without one never reach the server). */
export const razmerAutosaveCellKey = (row: RazmerRow, field: RazmerAutosaveField): string => {
  const rowKey = row.osRowId || row.sourceRowKey || "";
  return rowKey ? `${rowKey}:${field}` : "";
};

const normalizeValue = (value: string | undefined): string => {
  const text = (value || "").trim();
  const parsed = parseDecimalInput(text);
  return parsed === null ? text : String(parsed);
};

const snapshotRows = (rows: RazmerRow[]): Map<string, string> => {
  const picture = new Map<string, string>();
  rows.forEach((row) => {
    RAZMER_AUTOSAVE_FIELDS.forEach((field) => {
      const key = razmerAutosaveCellKey(row, field);
      if (key) picture.set(key, normalizeValue(row[field]));
    });
  });
  return picture;
};

/**
 * After a quiet save the server echo is merged back into the rows. Anything the operator
 * typed into one of the three cells WHILE that save was running must survive the merge —
 * otherwise the cell being typed into would snap back to the value that was sent.
 * Returns the rows to show and whether any such local edit was kept (= still dirty).
 */
export const keepRazmerLocalEdits = (
  latestRows: RazmerRow[],
  sentRows: RazmerRow[],
  mergedRows: RazmerRow[],
): { rows: RazmerRow[]; keptLocalEdits: boolean } => {
  const byKey = (rows: RazmerRow[]) => new Map(rows.filter((r) => r.osRowId).map((r) => [r.osRowId as string, r]));
  const latestByKey = byKey(latestRows);
  const sentByKey = byKey(sentRows);
  let keptLocalEdits = false;
  const rows = mergedRows.map((row) => {
    const latest = row.osRowId ? latestByKey.get(row.osRowId) : undefined;
    const sent = row.osRowId ? sentByKey.get(row.osRowId) : undefined;
    if (!latest || !sent) return row;
    const patch: Partial<RazmerRow> = {};
    RAZMER_AUTOSAVE_FIELDS.forEach((field) => {
      if ((latest[field] || "") !== (sent[field] || "")) patch[field] = latest[field];
    });
    if (Object.keys(patch).length === 0) return row;
    keptLocalEdits = true;
    return { ...row, ...patch, confirmed: latest.confirmed };
  });
  return { rows, keptLocalEdits };
};

interface Options {
  enabled: boolean;
  rows: RazmerRow[];
  /** Nothing waits for Save — the hook re-seeds its "last saved" picture from `rows`. */
  isClean: boolean;
  canSave: (rows: RazmerRow[]) => boolean;
  /** The screen's save path in quiet mode; resolves true on success (errors are toasted there). */
  save: (rows: RazmerRow[]) => Promise<boolean>;
}

const SAVED_MARK_MS = 2000;

export const useRNRazmerAutosave = ({ enabled, rows, isClean, canSave, save }: Options) => {
  const [cellSaveStatus, setCellSaveStatus] = useState<RazmerCellSaveStatusMap>({});
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const canSaveRef = useRef(canSave);
  canSaveRef.current = canSave;
  const saveRef = useRef(save);
  saveRef.current = save;
  const baselineRef = useRef<Map<string, string>>(new Map());
  const runningRef = useRef(false);
  const pendingKeysRef = useRef<Set<string>>(new Set());
  const savedTimersRef = useRef<Map<string, number>>(new Map());

  // Re-seed only when the screen is clean and no autosave is in flight: a load, a manual
  // Save or a ✓-save all end here. While dirty the baseline must stay what was last saved.
  useEffect(() => {
    if (isClean && !runningRef.current) baselineRef.current = snapshotRows(rows);
  }, [rows, isClean]);

  useEffect(() => () => {
    savedTimersRef.current.forEach((timer) => window.clearTimeout(timer));
  }, []);

  const setStatus = useCallback((keys: string[], status: RazmerCellSaveStatus | null) => {
    setCellSaveStatus((prev) => {
      const next = { ...prev };
      keys.forEach((key) => {
        if (status) next[key] = status;
        else delete next[key];
      });
      return next;
    });
  }, []);

  const flashSaved = useCallback((keys: string[]) => {
    setStatus(keys, "saved");
    keys.forEach((key) => {
      window.clearTimeout(savedTimersRef.current.get(key));
      savedTimersRef.current.set(key, window.setTimeout(() => {
        savedTimersRef.current.delete(key);
        setStatus([key], null);
      }, SAVED_MARK_MS));
    });
  }, [setStatus]);

  const runSave = useCallback(async (keys: string[]) => {
    runningRef.current = true;
    const sent = rowsRef.current;
    let ok = false;
    try {
      ok = await saveRef.current(sent);
    } catch {
      ok = false;
    }
    runningRef.current = false;
    if (ok) {
      snapshotRows(sent).forEach((value, key) => baselineRef.current.set(key, value));
      flashSaved(keys);
    } else {
      setStatus(keys, null);
    }
    // Exactly one follow-up save for everything that blurred meanwhile.
    const pending = Array.from(pendingKeysRef.current);
    pendingKeysRef.current.clear();
    if (pending.length === 0) return;
    if (canSaveRef.current(rowsRef.current)) void runSave(pending);
    else setStatus(pending, null);
  }, [flashSaved, setStatus]);

  /** Called by the table when the operator leaves one of the three cells (Enter/Tab/blur). */
  const commit = useCallback((rowIndex: number, field: RazmerAutosaveField) => {
    if (!enabled) return;
    const row = rowsRef.current[rowIndex];
    if (!row) return;
    const key = razmerAutosaveCellKey(row, field);
    if (!key) return;
    if (normalizeValue(row[field]) === (baselineRef.current.get(key) ?? "")) return;
    if (!canSaveRef.current(rowsRef.current)) return;
    setStatus([key], "saving");
    if (runningRef.current) {
      pendingKeysRef.current.add(key);
      return;
    }
    void runSave([key]);
  }, [enabled, runSave, setStatus]);

  return { commit, cellSaveStatus };
};

export default useRNRazmerAutosave;
